(() => {
  'use strict';

  const MAP_VIEW_STORAGE_KEY = 'anteage:provider-locator:map-view:v2';
  const FILTER_STORAGE_KEY = 'anteage:provider-locator:filters:v2';

  // Version only the provider source/tiles, leaving basemap assets cached.
  // A stable five-minute bucket avoids reusing the old static-upload tiles
  // across page loads without creating a different URL for every request.
  function locatorRequest(url, resourceType) {
    if (!['Source', 'Tile'].includes(resourceType)) return { url };
    try {
      const request = new URL(url);
      if (request.hostname !== 'api.mapbox.com'
        || !request.pathname.includes('anteage.c78cqodcgfgr')) return { url };
      request.searchParams.set('locator_revision', `v2-sync-${Math.floor(Date.now() / 300000)}`);
      return { url: request.toString() };
    } catch (_error) {
      return { url };
    }
  }

  const FALLBACK_IMAGES = {
    md: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/MDBanner_B_1500x.jpg?v=1674165433',
    pro: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/ProBanner_B_1500x.jpg?v=1674165433',
  };

  const SERVICE_ICONS = {
    'body contouring': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/noun-body-shaping-4655384-4B858E.svg?v=1674148521',
    'chemical peels': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Chemical_Peel.svg?v=1757373800',
    co2: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/C02.svg?v=1757373831',
    'cosmetic surgery': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Cosmetic_Surgery.svg?v=1757373867',
    dermaplaning: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Dermaplaning.svg?v=1757373875',
    dermatology: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Dermatology.svg?v=1757373884',
    exosomes: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Exosomes.svg?v=1757373925',
    fillers: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Filler.svg?v=1757373907',
    'general skincare': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/General_Skincare.svg?v=1757373944',
    'hair treatments': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Hair_Treatments.svg?v=1757373955',
    ipl: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/IPL.svg?v=1757373972',
    laser: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Laser.svg?v=1757373980',
    'laser hair removal': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/LaserHairRemoval.svg?v=1757373988',
    microneedling: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Microneedling.svg?v=1757373997',
    neurotoxins: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Neurotoxins.svg?v=1757374007',
    radiofrequency: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Radiofrequency.svg?v=1757374017',
    'tattoo removal': 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Tattoo_Removal.svg?v=1757374027',
    other: 'https://cdn.shopify.com/s/files/1/0067/5636/8435/files/Other.svg?v=1757374045',
  };

  const VALUE_ALIASES = new Map([
    ['home care', 'homecare'],
    ['homecare', 'homecare'],
    ['treatments', 'treatment'],
    ['treatment', 'treatment'],
    ['biosome', 'biosomes'],
    ['biosomes', 'biosomes'],
    ['exosome', 'exosomes'],
    ['exosomes', 'exosomes'],
    ['growth factor', 'growth_factors'],
    ['growth factors', 'growth_factors'],
    ['pearl', 'pearl'],
    ['skincare', 'skincare'],
    ['skin care', 'skincare'],
    ['hair restoration', 'hair_treatment'],
    ['hair treatment', 'hair_treatment'],
    ['hair treatments', 'hair_treatment'],
    ['vaginal rejuvenation', 'vrs'],
    ['vrs', 'vrs'],
  ]);

  function asString(value) {
    return value == null ? '' : String(value).trim();
  }

  function asBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    return ['true', '1', 'yes', 'active'].includes(asString(value).toLowerCase());
  }

  function normalizedKey(value) {
    return asString(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[_-]+/g, ' ')
      .replace(/[^a-z0-9 ]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function taxonomyKey(value) {
    const normalized = normalizedKey(value);
    return VALUE_ALIASES.get(normalized) || normalized.replace(/\s+/g, '_');
  }

  function splitValues(value, taxonomy = false) {
    if (Array.isArray(value)) {
      return value.map(item => taxonomy ? taxonomyKey(item) : normalizedKey(item)).filter(Boolean);
    }
    return asString(value)
      .split(/[;,|]/)
      .map(item => taxonomy ? taxonomyKey(item) : normalizedKey(item))
      .filter(Boolean);
  }

  function splitDisplayValues(value) {
    if (Array.isArray(value)) return value.map(asString).filter(Boolean);
    return asString(value).split(/[;,|]/).map(asString).filter(Boolean);
  }

  function safeUrl(value) {
    const input = asString(value);
    if (!input) return '';
    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(input) ? input : `https://${input}`;
    try {
      const url = new URL(candidate, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      if (url.protocol === 'http:') url.protocol = 'https:';
      return url.toString();
    } catch (_error) {
      return '';
    }
  }

  function safeEmail(value) {
    const email = asString(value).toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
  }

  function phoneDial(value) {
    const input = asString(value);
    if (!input) return '';
    const extension = input.match(/(?:ext\.?|x|#)\s*(\d{1,6})\s*$/i)?.[1];
    const hasPlus = input.trim().startsWith('+');
    const digits = input.replace(/\D/g, '');
    if (digits.length < 7) return '';
    let dial = hasPlus ? `+${digits}` : digits.length === 10 ? `+1${digits}` : `+${digits}`;
    if (extension) dial += `;ext=${extension}`;
    return `tel:${dial}`;
  }

  function publicDescription(value) {
    return stripHtml(value).replace(/\b(?:ThermiVa\s+)?(?:vaginal rejuvenation|VRS)\b(?:\s*,\s*|\s+and\s+)?/gi, '').replace(/[ \t]+/g, ' ').trim();
  }

  function stripHtml(value) {
    const template = document.createElement('template');
    template.innerHTML = asString(value)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
    template.content.querySelectorAll('script, style, noscript').forEach(node => node.remove());
    return asString(template.content.textContent)
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\s*\n\s*/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace('Enter your description here...', '')
      .trim();
  }

  function slugify(value) {
    return asString(value)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'provider';
  }

  function getCoordinates(feature) {
    const coordinates = feature?.geometry?.coordinates || feature?._geometry?.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    const longitude = Number(coordinates[0]);
    const latitude = Number(coordinates[1]);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
    if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return null;
    return [longitude, latitude];
  }

  function distanceBetween(a, b) {
    const toRadians = degrees => degrees * Math.PI / 180;
    const earthRadiusMiles = 3958.8;
    const latitudeDelta = toRadians(b[1] - a[1]);
    const longitudeDelta = toRadians(b[0] - a[0]);
    const latitudeA = toRadians(a[1]);
    const latitudeB = toRadians(b[1]);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
    return 2 * earthRadiusMiles * Math.asin(Math.sqrt(haversine));
  }

  function normalizeProvider(feature) {
    const properties = feature?.properties || {};
    const canonicalId = asString(
      properties.locator_entry_id
      || properties.hs_location_entry_id
      || feature?.id
      || properties.location_id
    );
    const coordinates = getCoordinates(feature);
    const name = asString(properties.name || properties.title);
    if (!canonicalId || !coordinates || !name) return null;
    if (properties.location_active != null && !asBoolean(properties.location_active)) return null;

    const legacyId = asString(properties.location_id);
    const designation = taxonomyKey(properties.designation || properties.account_type).includes('md') ? 'md' : 'pro';
    const addressLine1 = asString(properties.address_1);
    const addressLine2 = asString(properties.address_2);
    const city = asString(properties.city);
    const region = asString(properties.province || properties.state);
    const postalCode = asString(properties.zip);
    const country = asString(properties.country);
    const formattedAddress = asString(properties.place_name)
      || [addressLine1, addressLine2, city, region, postalCode, country].filter(Boolean).join(', ');
    const bookingEmail = safeEmail(properties.booking_email);
    const phoneDisplay = asString(properties.phone_number);
    const website = safeUrl(properties.website_url);
    const onlineOrdering = safeUrl(properties.online_sales_link);

    return {
      id: canonicalId,
      legacyId,
      aliases: [canonicalId, legacyId, asString(properties.hs_location_entry_id)].filter(Boolean),
      name,
      providerType: asString(properties.provider_type || properties.type_of_provider),
      description: publicDescription(properties.description),
      fullDescription: publicDescription(properties.full_description),
      designation,
      accountType: asString(properties.account_type),
      offersMd: asBoolean(properties.offers_md),
      // The canonical flag is authoritative, including an explicit false.
      preferredProvider: properties.preferred_provider != null
        ? asBoolean(properties.preferred_provider)
        : asBoolean(properties.loyalty___rewards_status)
          || asBoolean(properties.zloyalty___rewards_status),
      onlineOrdering: asBoolean(properties.online_ordering) || Boolean(onlineOrdering),
      taxonomy: {
        offerings: splitValues(properties.offerings || properties.product_types, true),
        ingredients: splitValues(properties.ingredients || properties.active_ingredient_types, true),
        targets: splitValues(properties.targets, true).filter(value => value !== 'vrs'),
      },
      // Keep retired taxonomy in HubSpot without displaying it publicly.
      services: splitDisplayValues(properties.services).filter(value => taxonomyKey(value) !== 'vrs'),
      address: {
        line1: addressLine1,
        line2: addressLine2,
        city,
        region,
        postalCode,
        country,
        formatted: formattedAddress,
      },
      contact: {
        bookingEmail,
        phoneDisplay,
        phoneDial: phoneDial(phoneDisplay),
      },
      links: {
        website,
        onlineOrdering,
        facebook: safeUrl(properties.facebook_url),
        instagram: safeUrl(properties.instagram_url),
        tiktok: safeUrl(properties.tiktok_url),
        twitter: safeUrl(properties.twitter_url),
        realself: safeUrl(properties.realself_url),
        yelp: safeUrl(properties.yelp_url),
      },
      media: {
        profilePhoto: safeUrl(properties.location_profile_photo),
        logo: safeUrl(properties.logo),
      },
      hours: stripHtml(properties.hours),
      coordinates,
      feature,
    };
  }

  class ProviderLocator {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.mapNode = root.querySelector('[data-locator-map]');
      this.resultsNode = root.querySelector('[data-locator-results]');
      this.statusNode = root.querySelector('[data-locator-status]');
      this.profileNode = root.querySelector('[data-provider-profile-modal]');
      this.filterModal = root.querySelector('custom-modal[modal-id="locator-filters"]');
      this.map = null;
      this.profileMap = null;
      this.popup = null;
      this.geocoder = null;
      this.sourceId = '';
      this.sourceLayer = '';
      this.originalFilter = null;
      this.providers = new Map();
      this.aliases = new Map();
      this.filteredIds = null;
      this.activeProvider = null;
      this.selectedProviderId = '';
      this.pendingHash = window.location.hash;
      this.filterSnapshot = '';
      this.refreshTimer = null;
      this.renderTimer = null;
      this.profileHideTimer = null;
      this.lastFocusedElement = null;
      this.isReady = false;
      this.isBootstrapping = false;
      this.maxResults = Number(this.config.maxResults || 150);
    }

    readConfig() {
      const node = this.root.querySelector('[data-locator-config]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[Provider Locator] Invalid configuration.', error);
        return {};
      }
    }

    hasExplicitMapLocation() {
      const requestedLocation = asString(new URLSearchParams(window.location.search).get('location'));
      if (requestedLocation) return true;
      const hash = asString(window.location.hash).replace(/^#/, '').toLowerCase();
      return Boolean(hash && hash !== 'contact-form' && /(?:^|-)\d+$/.test(hash));
    }

    defaultMapView() {
      return {
        center: this.config.initialCenter || [-96.7, 37.5],
        zoom: Number(this.config.initialZoom || 2.25),
      };
    }

    minimumMapZoom() {
      const bounds = Array.isArray(this.config.minZoomBounds)
        ? this.config.minZoomBounds
        : [[-128, 22], [-65, 52]];
      try {
        const camera = this.map?.cameraForBounds(bounds, { padding: 0 });
        return Number.isFinite(camera?.zoom) ? Math.max(0, camera.zoom) : 2.25;
      } catch (_error) {
        return 2.25;
      }
    }

    applyMinimumMapZoom() {
      if (!this.map || !this.isReady) return;
      const minimumZoom = this.minimumMapZoom();
      this.map.setMinZoom(minimumZoom);
      if (this.map.getZoom() < minimumZoom) this.map.setZoom(minimumZoom);
    }

    readStoredMapView() {
      if (this.hasExplicitMapLocation()) return null;
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(MAP_VIEW_STORAGE_KEY));
        const longitude = Number(stored?.center?.[0]);
        const latitude = Number(stored?.center?.[1]);
        const zoom = Number(stored?.zoom);
        if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return null;
        if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return null;
        if (!Number.isFinite(zoom) || zoom < 0 || zoom > 16) return null;
        return { center: [longitude, latitude], zoom };
      } catch (_error) {
        return null;
      }
    }

    saveMapView() {
      if (!this.isReady || !this.map) return;
      try {
        const center = this.map.getCenter();
        const longitude = ((center.lng + 180) % 360 + 360) % 360 - 180;
        window.sessionStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify({
          center: [longitude, center.lat],
          zoom: this.map.getZoom(),
        }));
      } catch (_error) {
        // The locator remains fully functional when session storage is unavailable.
      }
    }

    init() {
      this.restoreStoredFilters();
      this.bindDomEvents();
      this.bindProfileForm();
      this.bindLocationParamCleanup();
      this.root.dataset.locatorState = 'loading';
      if (!this.mapNode || !this.resultsNode || !this.config.accessToken || !this.config.styleUrl) {
        this.fail('The provider locator is unavailable right now. Please try again later.');
        return;
      }
      if (!window.mapboxgl) {
        this.fail('The map could not be loaded. Please refresh the page and try again.');
        return;
      }

      window.mapboxgl.accessToken = this.config.accessToken;
      this.map = new window.mapboxgl.Map({
        container: this.mapNode,
        style: this.config.styleUrl,
        transformRequest: locatorRequest,
        center: this.config.indexCenter || [0, 0],
        zoom: Number(this.config.indexZoom ?? 0),
        maxZoom: 16,
      });
      this.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }));
      this.addGeocoder();
      this.map.on('load', () => this.handleMapLoad());
      this.map.on('error', event => this.handleMapError(event));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.map?.resize();
          this.applyMinimumMapZoom();
        });
        this.resizeObserver.observe(this.root.querySelector('.locator-layout') || this.root);
      }
    }

    bindLocationParamCleanup() {
      if (!new URLSearchParams(window.location.search).has('location') || !this.mapNode) return;
      const clearOnInteraction = event => {
        if (event.isTrusted) this.clearLocationParam();
      };
      this.mapNode.addEventListener('pointerdown', clearOnInteraction, { capture: true, once: true });
      this.mapNode.addEventListener('touchstart', clearOnInteraction, { capture: true, once: true, passive: true });
      this.mapNode.addEventListener('wheel', clearOnInteraction, { capture: true, once: true, passive: true });
      this.mapNode.addEventListener('keydown', clearOnInteraction, { capture: true, once: true });
    }

    clearLocationParam() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('location')) return;
      url.searchParams.delete('location');
      history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    restoreStoredFilters() {
      let stored;
      try {
        stored = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY));
      } catch (_error) {
        return;
      }
      if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return;
      this.root.querySelectorAll('[data-locator-filter]').forEach(input => {
        const values = stored[input.dataset.filterGroup];
        input.checked = Array.isArray(values) && values.includes(input.dataset.filterValue);
      });
    }

    saveFilters(filters = this.selectedFilters()) {
      try {
        if (Object.keys(filters).length > 0) {
          window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        } else {
          window.sessionStorage.removeItem(FILTER_STORAGE_KEY);
        }
      } catch (_error) {
        // The locator remains fully functional when session storage is unavailable.
      }
    }

    addGeocoder() {
      if (!window.MapboxGeocoder) return;
      const configuredZoom = Number(this.config.searchZoom ?? 9);
      const searchZoom = Number.isFinite(configuredZoom) ? Math.max(0, Math.min(16, configuredZoom)) : 9;
      this.geocoder = new window.MapboxGeocoder({
        accessToken: this.config.accessToken,
        mapboxgl: window.mapboxgl,
        marker: false,
        zoom: searchZoom,
        // Postcodes can return bounds, which otherwise override `zoom`.
        flyTo: { maxZoom: searchZoom },
        placeholder: 'Search city, state, or ZIP',
        countries: 'us,ca,mx,pr',
        types: 'postcode,place,locality,neighborhood,address',
      });
      this.map.addControl(this.geocoder, 'top-left');
    }

    handleMapLoad() {
      const layer = this.map.getLayer(this.config.layerId);
      if (!layer) {
        this.fail('The provider map is missing its location layer. Please try again later.');
        return;
      }

      this.sourceId = layer.source;
      this.sourceLayer = layer['source-layer'] || layer.sourceLayer || '';
      this.originalFilter = this.map.getFilter(this.config.layerId) || null;

      this.map.on('click', this.config.layerId, event => this.handleMapClick(event));
      this.map.on('mouseenter', this.config.layerId, () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', this.config.layerId, () => { this.map.getCanvas().style.cursor = ''; });
      this.map.on('movestart', () => this.removePopup());
      this.map.on('moveend', () => {
        this.scheduleRender();
        this.saveMapView();
      });
      this.map.on('sourcedata', event => {
        if (event.sourceId === this.sourceId && event.isSourceLoaded) this.scheduleIndexRefresh();
      });

      this.map.once('idle', () => this.bootstrapProviders());
      window.setTimeout(() => {
        if (!this.isReady) this.bootstrapProviders();
      }, 2500);
    }

    async bootstrapProviders() {
      if (this.isReady || this.isBootstrapping) return;
      this.isBootstrapping = true;
      this.refreshProviderIndex();
      const indexViews = Array.isArray(this.config.indexViews)
        ? this.config.indexViews
        : [[0, 0, 0], [-96.7, 37.5, 2.25], [135, -25, 2]];
      for (const view of indexViews.slice(1)) {
        if (!Array.isArray(view) || view.length < 3) continue;
        await this.loadIndexView([Number(view[0]), Number(view[1])], Number(view[2]));
        this.refreshProviderIndex();
      }
      if (this.providers.size === 0) {
        this.setStatus('Loading providers…');
        this.isBootstrapping = false;
        return;
      }
      this.isReady = true;
      this.applyMinimumMapZoom();
      this.map.jumpTo(this.readStoredMapView() || this.defaultMapView());
      this.applyFilters();
      this.renderResults();
      this.applyInitialSearch();
      this.syncProfileFromUrl();
      if (!this.hasExplicitMapLocation()) this.saveMapView();
      this.root.dataset.locatorState = 'ready';
      this.mapNode.setAttribute('aria-busy', 'false');
      this.isBootstrapping = false;
    }

    loadIndexView(center, zoom) {
      return new Promise(resolve => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        this.map.once('idle', finish);
        this.map.jumpTo({ center, zoom });
        window.setTimeout(finish, 5000);
      });
    }

    handleMapError(event) {
      const message = event?.error?.message || '';
      if (/style|source|layer/i.test(message)) console.error('[Provider Locator] Map error:', message);
    }

    querySourceFeatures() {
      if (!this.map || !this.sourceId) return [];
      try {
        return this.sourceLayer
          ? this.map.querySourceFeatures(this.sourceId, { sourceLayer: this.sourceLayer })
          : this.map.querySourceFeatures(this.sourceId);
      } catch (error) {
        console.error('[Provider Locator] Could not read provider source.', error);
        return [];
      }
    }

    refreshProviderIndex() {
      const features = this.querySourceFeatures();
      let added = 0;
      features.forEach(feature => {
        const provider = normalizeProvider(feature);
        if (!provider) return;
        if (!this.providers.has(provider.id)) added += 1;
        this.providers.set(provider.id, provider);
        provider.aliases.forEach(alias => this.aliases.set(alias.toLowerCase(), provider.id));
        this.aliases.set(`${slugify(provider.name)}-${provider.id}`.toLowerCase(), provider.id);
        if (provider.legacyId) {
          this.aliases.set(`${slugify(provider.name)}-${provider.legacyId}`.toLowerCase(), provider.id);
        }
      });

      if (added > 0 && this.isReady) {
        this.applyFilters();
        this.scheduleRender();
        if (this.pendingHash) this.syncProfileFromUrl();
      }
      return added;
    }

    scheduleIndexRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshProviderIndex(), 100);
    }

    scheduleRender() {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = window.setTimeout(() => this.renderResults(), 80);
    }

    providersInView() {
      if (!this.map) return [];
      const bounds = this.map.getBounds();
      const center = this.map.getCenter();
      const centerCoordinates = [center.lng, center.lat];
      return [...this.providers.values()]
        .filter(provider => (!this.filteredIds || this.filteredIds.has(provider.id)) && bounds.contains(provider.coordinates))
        .sort((a, b) => distanceBetween(centerCoordinates, a.coordinates) - distanceBetween(centerCoordinates, b.coordinates));
    }

    renderResults() {
      if (!this.isReady || !this.resultsNode) return;
      const providers = this.providersInView();
      const visibleProviders = providers.slice(0, this.maxResults);
      const fragment = document.createDocumentFragment();
      visibleProviders.forEach(provider => fragment.appendChild(this.createProviderCard(provider)));
      this.resultsNode.replaceChildren(fragment);

      const hasFilters = this.hasActiveFilters();
      if (providers.length === 0) {
        this.resultsNode.appendChild(this.createEmptyState());
        if (hasFilters) {
          this.setStatus('No providers found matching your filters.', { editFilters: true });
        } else {
          this.setStatus('No providers in this area. Try moving the map or changing your filters.');
        }
      } else if (providers.length > visibleProviders.length) {
        this.setStatus(
          hasFilters
            ? `Showing the nearest ${visibleProviders.length} of ${providers.length} providers matching your filters.`
            : `Showing the nearest ${visibleProviders.length} of ${providers.length} providers in this area.`,
          { editFilters: hasFilters }
        );
      } else {
        this.setStatus(
          hasFilters
            ? `${providers.length} provider${providers.length === 1 ? '' : 's'} found matching your filters.`
            : `${providers.length} provider${providers.length === 1 ? '' : 's'} in this area.`,
          { editFilters: hasFilters }
        );
      }

      if (this.selectedProviderId) this.highlightCard(this.selectedProviderId, false);
    }

    createProviderCard(provider) {
      const card = document.createElement('article');
      card.className = 'location-card';
      card.dataset.locationId = provider.id;

      const selectButton = document.createElement('button');
      selectButton.type = 'button';
      selectButton.className = 'location-card__select';
      selectButton.dataset.providerSelect = provider.id;
      selectButton.setAttribute('aria-label', `Show ${provider.name} on the map`);

      const cardImage = provider.media.profilePhoto;
      if (cardImage && !Object.values(FALLBACK_IMAGES).includes(cardImage)) {
        const imageWrap = document.createElement('span');
        imageWrap.className = 'location-logo--wrapper';
        const image = document.createElement('img');
        image.src = cardImage;
        image.alt = '';
        image.loading = 'lazy';
        image.addEventListener('error', () => imageWrap.remove(), { once: true });
        imageWrap.appendChild(image);
        selectButton.appendChild(imageWrap);
      }

      const content = document.createElement('span');
      content.className = 'location-card__content';
      if (provider.providerType) {
        const type = document.createElement('span');
        type.className = 'SectionHeader__SubHeading Heading u-h7';
        type.textContent = provider.providerType;
        content.appendChild(type);
      }
      const title = document.createElement('span');
      title.className = 'location-card__title';
      title.textContent = provider.name;
      content.appendChild(title);
      if (provider.address.formatted) {
        const address = document.createElement('span');
        address.className = 'minimal--content';
        address.textContent = provider.address.formatted;
        content.appendChild(address);
      }
      selectButton.appendChild(content);
      card.appendChild(selectButton);

      const callouts = this.providerCallouts(provider);
      if (callouts.length) {
        const calloutWrap = document.createElement('div');
        calloutWrap.className = 'location-card--callouts';
        callouts.forEach(label => calloutWrap.appendChild(this.createCallout(label)));
        card.appendChild(calloutWrap);
      }

      if (provider.description) {
        const description = document.createElement('p');
        description.className = 'default--content';
        description.textContent = provider.description;
        card.appendChild(description);
      }

      const profileLink = document.createElement('a');
      profileLink.className = 'profile-link';
      profileLink.href = this.providerHash(provider);
      profileLink.dataset.providerProfile = provider.id;
      profileLink.textContent = 'View Profile';
      card.appendChild(profileLink);
      return card;
    }

    providerCallouts(provider) {
      const callouts = [];
      if (provider.preferredProvider) callouts.push('Preferred Provider');
      if (provider.offersMd) callouts.push('Offers AnteAGE MD');
      if (provider.taxonomy.offerings.includes('treatment')) callouts.push('Offers Treatments');
      if (provider.taxonomy.offerings.includes('homecare')) callouts.push('Offers Home Care');
      return callouts;
    }

    createCallout(label) {
      const callout = document.createElement('span');
      callout.className = 'location-card--callout';
      callout.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><polygon fill="currentColor" points="9,20 2,13 5,10 9,14 19,4 22,7"></polygon></svg>';
      const text = document.createElement('span');
      text.textContent = label;
      callout.appendChild(text);
      return callout;
    }

    createEmptyState() {
      const card = document.createElement('div');
      card.className = 'location-card no-locations--card';
      const title = document.createElement('h3');
      title.textContent = 'No locations found. Update your search or filters and try again.';
      card.appendChild(title);
      const actions = document.createElement('div');
      actions.className = 'mt1';
      const adjust = document.createElement('button');
      adjust.type = 'button';
      adjust.className = 'Button Button--primary';
      adjust.dataset.action = 'open-custom-modal';
      adjust.dataset.modalId = 'locator-filters';
      adjust.textContent = 'Adjust Filters';
      actions.appendChild(adjust);
      if (this.hasActiveFilters()) {
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'Button Button--secondary';
        reset.dataset.action = 'reset-all-filters';
        reset.textContent = 'Reset Filters';
        actions.appendChild(reset);
      }
      card.appendChild(actions);
      return card;
    }

    handleMapClick(event) {
      const feature = event?.features?.[0];
      if (!feature) return;
      const normalized = normalizeProvider(feature);
      if (!normalized) return;
      const provider = this.providers.get(normalized.id) || normalized;
      if (!this.providers.has(provider.id)) this.providers.set(provider.id, provider);
      this.selectProvider(provider, { openPopup: true, scrollCard: true });
    }

    selectProvider(provider, options = {}) {
      this.selectedProviderId = provider.id;
      if (options.openPopup !== false) this.openPopup(provider);
      this.highlightCard(provider.id, options.scrollCard !== false);
    }

    openPopup(provider) {
      this.removePopup();
      const popupContent = document.createElement('div');
      popupContent.className = 'provider-popup';
      const popupOptions = { offset: 8 };
      if (this.mapNode?.clientWidth > 640) {
        const point = this.map.project(provider.coordinates);
        const mapWidth = this.mapNode.clientWidth;
        const mapHeight = this.mapNode.clientHeight;
        const verticalAnchor = point.y >= mapHeight / 2 ? 'bottom' : 'top';
        const horizontalAnchor = point.x < 200 ? 'left' : point.x > mapWidth - 200 ? 'right' : '';
        const availableHeight = verticalAnchor === 'bottom' ? point.y : mapHeight - point.y;
        popupOptions.anchor = horizontalAnchor ? `${verticalAnchor}-${horizontalAnchor}` : verticalAnchor;
        popupContent.style.maxHeight = `${Math.max(220, Math.floor(availableHeight - 24))}px`;
      }
      if (provider.media.profilePhoto) {
        const imageWrap = document.createElement('div');
        imageWrap.className = 'popup-image--wrapper';
        const image = document.createElement('img');
        image.src = provider.media.profilePhoto;
        image.alt = '';
        image.addEventListener('error', () => imageWrap.remove(), { once: true });
        imageWrap.appendChild(image);
        popupContent.appendChild(imageWrap);
      }
      const meta = document.createElement('div');
      meta.className = 'popup-meta--content';
      const title = document.createElement('h3');
      title.textContent = provider.name;
      meta.appendChild(title);
      if (provider.address.formatted) {
        const address = document.createElement('p');
        address.className = 'popup-address--content';
        address.textContent = provider.address.formatted;
        meta.appendChild(address);
      }
      if (provider.description) {
        const description = document.createElement('p');
        description.className = 'popup-location--description';
        description.textContent = provider.description;
        meta.appendChild(description);
      }
      const link = document.createElement('a');
      link.className = 'profile-link';
      link.href = this.providerHash(provider);
      link.dataset.providerProfile = provider.id;
      link.textContent = 'View Profile';
      meta.appendChild(link);
      popupContent.appendChild(meta);

      this.popup = new window.mapboxgl.Popup(popupOptions)
        .setLngLat(provider.coordinates)
        .setDOMContent(popupContent)
        .addTo(this.map);
    }

    removePopup() {
      if (this.popup) {
        this.popup.remove();
        this.popup = null;
      }
    }

    highlightCard(providerId, shouldScroll = true) {
      this.resultsNode?.querySelectorAll('.location-card.clicked-open').forEach(card => card.classList.remove('clicked-open'));
      const card = [...(this.resultsNode?.querySelectorAll('.location-card') || [])]
        .find(node => node.dataset.locationId === providerId);
      if (!card) return;
      card.classList.add('clicked-open');
      if (!shouldScroll || !this.resultsNode) return;

      const resultsRect = this.resultsNode.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const top = this.resultsNode.scrollTop + cardRect.top - resultsRect.top;
      this.resultsNode.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    bindDomEvents() {
      this.root.addEventListener('click', event => {
        const profileLink = event.target.closest('[data-provider-profile]');
        if (profileLink) {
          event.preventDefault();
          const provider = this.providers.get(profileLink.dataset.providerProfile);
          if (provider) this.openProfile(provider, { pushHistory: true });
          return;
        }

        const selectButton = event.target.closest('[data-provider-select]');
        if (selectButton) {
          const provider = this.providers.get(selectButton.dataset.providerSelect);
          if (provider) this.selectProvider(provider, { openPopup: true, scrollCard: false });
          return;
        }

        const closeButton = event.target.closest('[data-profile-close]');
        if (closeButton) {
          event.preventDefault();
          this.requestProfileClose();
          return;
        }

        const scrollLink = event.target.closest('[data-profile-scroll]');
        if (scrollLink) {
          event.preventDefault();
          this.scrollProfileTo(scrollLink.dataset.profileScroll);
          return;
        }

        const clearGroup = event.target.closest('[data-action="clear-filter-group"]');
        if (clearGroup) {
          event.preventDefault();
          this.clearFilterGroup(clearGroup.dataset.filterGroup);
          return;
        }

        const reset = event.target.closest('[data-action="reset-all-filters"]');
        if (reset) {
          event.preventDefault();
          event.stopPropagation();
          this.resetFilters();
          return;
        }
      });

      this.root.addEventListener('change', event => {
        if (event.target.matches('[data-locator-filter]')) this.applyFilters();
      });

      this.root.addEventListener('custom-modal:open', event => {
        if (event.detail?.modalId !== 'locator-filters') return;
        this.filterModal?.setAttribute('aria-hidden', 'false');
        this.filterSnapshot = this.filterSignature();
        this.updateFilterControls();
      });
      this.root.addEventListener('custom-modal:close', event => {
        if (event.detail?.modalId !== 'locator-filters') return;
        this.filterModal?.setAttribute('aria-hidden', 'true');
        this.updateFilterControls(true);
      });

      window.addEventListener('popstate', () => this.syncProfileFromUrl());
      window.addEventListener('hashchange', () => this.syncProfileFromUrl());
      document.addEventListener('keydown', event => this.handleKeydown(event));
    }

    selectedFilters() {
      const groups = {};
      this.root.querySelectorAll('[data-locator-filter]:checked').forEach(input => {
        const group = input.dataset.filterGroup;
        const value = input.dataset.filterValue;
        if (!group || !value) return;
        if (!groups[group]) groups[group] = [];
        groups[group].push(value);
      });
      return groups;
    }

    filterSignature() {
      const groups = this.selectedFilters();
      return JSON.stringify(Object.fromEntries(
        Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([group, values]) => [group, [...values].sort()])
      ));
    }

    hasActiveFilters() {
      return Object.keys(this.selectedFilters()).length > 0;
    }

    providerMatches(provider, filters) {
      for (const [group, selectedValues] of Object.entries(filters)) {
        if (group === 'flags') {
          const flagMatches = {
            offers_md: provider.offersMd,
            online_ordering: provider.onlineOrdering,
            preferred_provider: provider.preferredProvider,
          };
          if (!selectedValues.every(value => flagMatches[value])) return false;
          continue;
        }

        let providerValues = [];
        if (group === 'provider_type') providerValues = [normalizedKey(provider.providerType)];
        else if (group === 'services') providerValues = provider.services.map(normalizedKey);
        else providerValues = provider.taxonomy[group] || [];
        const normalizedSelected = selectedValues.map(value => group === 'services' || group === 'provider_type' ? normalizedKey(value) : taxonomyKey(value));
        if (!normalizedSelected.some(value => providerValues.includes(value))) return false;
      }
      return true;
    }

    applyFilters() {
      const filters = this.selectedFilters();
      this.saveFilters(filters);
      const hasFilters = Object.keys(filters).length > 0;
      this.filteredIds = hasFilters
        ? new Set([...this.providers.values()].filter(provider => this.providerMatches(provider, filters)).map(provider => provider.id))
        : null;

      if (this.map && this.map.getLayer(this.config.layerId)) {
        let mapFilter = this.originalFilter;
        if (hasFilters) {
          const matchingIds = [...this.filteredIds];
          const idFilter = matchingIds.length
            ? ['in', ['to-string', ['get', 'locator_entry_id']], ['literal', matchingIds]]
            : ['==', 1, 0];
          mapFilter = this.originalFilter ? ['all', this.originalFilter, idFilter] : idFilter;
        }
        this.map.setFilter(this.config.layerId, mapFilter);
      }

      this.updateFilterControls();
      this.scheduleRender();
    }

    updateFilterControls(forceApplyDisabled = false) {
      const filters = this.selectedFilters();
      const activeGroups = new Set(Object.keys(filters));
      this.root.querySelectorAll('[data-action="clear-filter-group"]').forEach(button => {
        button.classList.toggle('hidden', !activeGroups.has(button.dataset.filterGroup));
      });
      const hasFilters = activeGroups.size > 0;
      this.root.querySelector('.filter-toggle--button')?.classList.toggle('active', hasFilters);
      const reset = this.root.querySelector('[data-content="filter-reset-button"]');
      const apply = this.root.querySelector('[data-content="filter-apply-button"]');
      if (reset) reset.disabled = !hasFilters;
      if (apply) apply.disabled = forceApplyDisabled || this.filterSignature() === this.filterSnapshot;
    }

    clearFilterGroup(group) {
      this.root.querySelectorAll(`[data-locator-filter][data-filter-group="${group}"]:checked`)
        .forEach(input => { input.checked = false; });
      this.applyFilters();
    }

    resetFilters() {
      this.root.querySelectorAll('[data-locator-filter]:checked').forEach(input => { input.checked = false; });
      this.applyFilters();
    }

    providerHash(provider) {
      return `#${slugify(provider.name)}-${provider.id}`;
    }

    resolveProviderFromHash(hash = window.location.hash) {
      let decoded;
      try {
        decoded = decodeURIComponent(asString(hash).replace(/^#/, '')).toLowerCase();
      } catch (_error) {
        return null;
      }
      if (!decoded || decoded === 'contact-form') return null;
      const directId = this.aliases.get(decoded);
      if (directId) return this.providers.get(directId) || null;
      const trailingId = decoded.match(/(?:^|-)(\d+)$/)?.[1];
      const resolvedId = trailingId ? this.aliases.get(trailingId) : null;
      return resolvedId ? this.providers.get(resolvedId) || null : null;
    }

    syncProfileFromUrl() {
      if (!this.isReady) {
        this.pendingHash = window.location.hash;
        return;
      }
      const provider = this.resolveProviderFromHash();
      if (provider) {
        this.pendingHash = '';
        this.map.jumpTo({
          center: provider.coordinates,
          zoom: Number(this.config.profileZoom || 15),
        });
        this.openProfile(provider, { pushHistory: false, focus: false });
      } else {
        this.pendingHash = window.location.hash;
        if (this.activeProvider) this.hideProfile({ restoreFocus: false });
      }
    }

    openProfile(provider, options = {}) {
      if (!this.profileNode) return;
      const { pushHistory = true, focus = true } = options;
      this.lastFocusedElement = document.activeElement;
      this.activeProvider = provider;
      this.renderProfile(provider);
      this.profileNode.hidden = false;
      this.profileNode.setAttribute('aria-hidden', 'false');
      window.clearTimeout(this.profileHideTimer);
      requestAnimationFrame(() => this.profileNode.classList.add('display-profile-modal'));
      document.documentElement.classList.add('no-scroll');
      this.buildProfileMap(provider);

      if (pushHistory && window.location.hash !== this.providerHash(provider)) {
        const state = { ...(history.state || {}), providerLocator: { id: provider.id } };
        history.pushState(state, '', this.providerHash(provider));
      }
      if (focus) window.setTimeout(() => this.profileNode.querySelector('[data-profile-close]')?.focus(), 50);
    }

    requestProfileClose() {
      if (history.state?.providerLocator) {
        history.back();
      } else {
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState(history.state, '', `${url.pathname}${url.search}`);
        this.hideProfile({ restoreFocus: true });
      }
    }

    hideProfile(options = {}) {
      if (!this.profileNode || this.profileNode.hidden) return;
      this.profileNode.classList.remove('display-profile-modal');
      this.profileNode.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');
      this.removeProfileMap();
      this.activeProvider = null;
      this.profileHideTimer = window.setTimeout(() => { this.profileNode.hidden = true; }, 460);
      if (options.restoreFocus !== false && this.lastFocusedElement?.focus) this.lastFocusedElement.focus();
    }

    renderProfile(provider) {
      this.profileNode.querySelectorAll('[data-profile-name]').forEach(node => { node.textContent = provider.name; });
      const subtitle = [provider.address.city, provider.address.region].filter(Boolean).join(', ')
        + ((provider.address.city || provider.address.region) && provider.providerType ? ' | ' : '')
        + provider.providerType;
      this.setText('[data-profile-subtitle]', subtitle);
      this.setText('[data-profile-description]', provider.description);
      this.setText('[data-profile-full-description]', provider.fullDescription);
      this.toggle('[data-profile-preferred]', provider.preferredProvider);

      const fallbackImage = FALLBACK_IMAGES[provider.designation] || FALLBACK_IMAGES.pro;
      const profileImage = this.profileNode.querySelector('[data-profile-image]');
      if (profileImage) {
        profileImage.src = provider.media.profilePhoto || fallbackImage;
        profileImage.alt = `${provider.name} location`;
        profileImage.onerror = () => {
          profileImage.onerror = null;
          profileImage.src = fallbackImage;
        };
      }

      const logo = this.profileNode.querySelector('[data-profile-logo]');
      const logoFallback = this.profileNode.querySelector('[data-profile-logo-fallback]');
      if (logo && provider.media.logo) {
        logo.src = provider.media.logo;
        logo.alt = `${provider.name} logo`;
        logo.hidden = false;
        if (logoFallback) logoFallback.hidden = true;
        logo.onerror = () => {
          logo.hidden = true;
          if (logoFallback) logoFallback.hidden = false;
        };
      } else {
        if (logo) {
          logo.hidden = true;
          logo.removeAttribute('src');
        }
        if (logoFallback) logoFallback.hidden = false;
      }

      const links = {
        ...provider.links,
        directions: this.directionsUrl(provider),
      };
      Object.entries(links).forEach(([key, value]) => this.setLinks(key, value));
      this.toggle('[data-profile-hero-actions]', Boolean(provider.contact.bookingEmail || provider.links.onlineOrdering));
      this.toggle('[data-profile-contact-link]', Boolean(provider.contact.bookingEmail), true);

      const hasAbout = Boolean(provider.fullDescription);
      const hasServices = provider.services.length > 0;
      this.toggle('[data-profile-about-section]', hasAbout || hasServices, true);
      this.toggle('[data-profile-about-copy]', hasAbout, true);
      this.toggle('[data-profile-services-section]', hasServices, true);
      this.renderServices(provider.services);

      this.setContactDetails(provider);
      this.toggle('[data-profile-contact-form-section]', Boolean(provider.contact.bookingEmail), true);
      this.resetProfileForm();
      const legacyId = this.profileNode.querySelector('[data-profile-legacy-id]');
      const canonicalId = this.profileNode.querySelector('[data-profile-canonical-id]');
      if (legacyId) legacyId.value = provider.legacyId || provider.id;
      if (canonicalId) canonicalId.value = provider.id;
    }

    setText(selector, value) {
      this.profileNode.querySelectorAll(selector).forEach(node => {
        node.textContent = value || '';
        node.hidden = !value;
      });
    }

    toggle(selector, visible, all = false) {
      const nodes = all ? this.profileNode.querySelectorAll(selector) : [this.profileNode.querySelector(selector)].filter(Boolean);
      nodes.forEach(node => { node.hidden = !visible; });
    }

    setLinks(key, value) {
      this.profileNode.querySelectorAll(`[data-profile-link="${key}"]`).forEach(link => {
        if (value) {
          link.href = value;
          link.hidden = false;
        } else {
          link.hidden = true;
          link.removeAttribute('href');
        }
      });
    }

    setContactDetails(provider) {
      const websiteLabel = this.profileNode.querySelector('[data-profile-website-label]');
      if (websiteLabel) {
        try { websiteLabel.textContent = new URL(provider.links.website).hostname.replace(/^www\./, ''); }
        catch (_error) { websiteLabel.textContent = provider.links.website; }
      }
      this.toggle('[data-profile-contact-row="website"]', Boolean(provider.links.website));

      const email = this.profileNode.querySelector('[data-profile-email]');
      const emailLink = this.profileNode.querySelector('[data-profile-email-link]');
      if (email) email.textContent = provider.contact.bookingEmail;
      if (emailLink) emailLink.href = provider.contact.bookingEmail ? `mailto:${provider.contact.bookingEmail}` : '';
      this.toggle('[data-profile-contact-row="email"]', Boolean(provider.contact.bookingEmail));

      const phone = this.profileNode.querySelector('[data-profile-phone]');
      const phoneLink = this.profileNode.querySelector('[data-profile-phone-link]');
      if (phone) phone.textContent = provider.contact.phoneDisplay;
      if (phoneLink) phoneLink.href = provider.contact.phoneDial;
      this.toggle('[data-profile-contact-row="phone"]', Boolean(provider.contact.phoneDisplay && provider.contact.phoneDial));

      const addressLine = [provider.address.line1, provider.address.line2].filter(Boolean).join(' ');
      const locality = [provider.address.city, provider.address.region, provider.address.postalCode].filter(Boolean).join(', ');
      const addressLineNode = this.profileNode.querySelector('[data-profile-address-line]');
      const localityNode = this.profileNode.querySelector('[data-profile-address-locality]');
      if (addressLineNode) addressLineNode.textContent = addressLine;
      if (localityNode) localityNode.textContent = locality;
      this.toggle('[data-profile-contact-row="address"]', Boolean(provider.address.formatted));

      const hours = this.profileNode.querySelector('[data-profile-hours]');
      if (hours) hours.textContent = provider.hours;
      this.toggle('[data-profile-contact-row="hours"]', Boolean(provider.hours));
    }

    directionsUrl(provider) {
      const destination = provider.address.formatted || provider.coordinates.slice().reverse().join(',');
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }

    renderServices(services) {
      const container = this.profileNode.querySelector('[data-profile-services]');
      if (!container) return;
      const fragment = document.createDocumentFragment();
      services.forEach(service => {
        const item = document.createElement('div');
        item.className = 'lpm_service-grid--item';
        const image = document.createElement('img');
        image.className = 'lpm_service--icon';
        image.src = SERVICE_ICONS[normalizedKey(service)] || SERVICE_ICONS.other;
        image.alt = '';
        const label = document.createElement('span');
        label.className = 'lpm_service--label';
        label.textContent = service;
        item.append(image, label);
        fragment.appendChild(item);
      });
      container.replaceChildren(fragment);
    }

    buildProfileMap(provider) {
      this.removeProfileMap();
      const parent = this.profileNode.querySelector('[data-profile-map]');
      if (!parent) return;
      const container = document.createElement('div');
      container.id = 'profile-map';
      container.className = 'provider-profile__map';
      parent.replaceChildren(container);
      const profileZoom = Number(this.config.profileZoom || 15);
      this.profileMap = new window.mapboxgl.Map({
        container,
        style: this.config.styleUrl,
        transformRequest: locatorRequest,
        center: provider.coordinates,
        zoom: profileZoom,
        minZoom: profileZoom,
        maxZoom: profileZoom,
        interactive: true,
        scrollZoom: false,
        boxZoom: false,
        dragRotate: false,
        pitchWithRotate: false,
        doubleClickZoom: false,
        touchZoomRotate: false,
        keyboard: false,
      });
      this.profileMap.once('load', () => {
        if (!this.profileMap?.getLayer(this.config.layerId)) return;
        const existing = this.profileMap.getFilter(this.config.layerId);
        const providerFilter = ['==', ['to-string', ['get', 'locator_entry_id']], provider.id];
        this.profileMap.setFilter(this.config.layerId, existing ? ['all', existing, providerFilter] : providerFilter);
      });
    }

    removeProfileMap() {
      if (this.profileMap) {
        this.profileMap.remove();
        this.profileMap = null;
      }
      this.profileNode?.querySelector('[data-profile-map]')?.replaceChildren();
    }

    scrollProfileTo(id) {
      const target = this.profileNode.querySelector(`#${id}`);
      if (!target) return;
      this.profileNode.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
    }

    handleKeydown(event) {
      if (!this.activeProvider || !this.profileNode || this.profileNode.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.requestProfileClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.profileNode.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(node => !node.hidden && node.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    bindProfileForm() {
      const form = this.profileNode?.querySelector('[data-profile-form]');
      if (!form) return;
      const updateValidity = () => {
        const submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = !form.checkValidity();
      };
      form.addEventListener('input', updateValidity);
      form.addEventListener('change', updateValidity);
      form.addEventListener('submit', event => this.submitProfileForm(event));
    }

    async submitProfileForm(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const submit = form.querySelector('[type="submit"]');
      const error = this.profileNode.querySelector('[data-profile-form-error]');
      const success = this.profileNode.querySelector('[data-profile-form-success]');
      if (!form.checkValidity() || !this.activeProvider?.contact.bookingEmail || !this.config.leadEndpoint) {
        form.reportValidity();
        return;
      }
      if (submit) submit.disabled = true;
      if (error) error.hidden = true;
      if (success) success.hidden = true;
      const formData = new FormData(form);
      formData.append('forward-to', this.activeProvider.contact.bookingEmail);
      try {
        const response = await fetch(this.config.leadEndpoint, { method: 'POST', body: formData });
        if (!response.ok) throw new Error(`Lead request failed with HTTP ${response.status}`);
        form.hidden = true;
        if (success) success.hidden = false;
      } catch (requestError) {
        console.error('[Provider Locator] Lead submission failed.', requestError);
        if (error) error.hidden = false;
        if (submit) submit.disabled = false;
      }
    }

    resetProfileForm() {
      const form = this.profileNode.querySelector('[data-profile-form]');
      const error = this.profileNode.querySelector('[data-profile-form-error]');
      const success = this.profileNode.querySelector('[data-profile-form-success]');
      if (form) {
        form.reset();
        form.hidden = false;
        const submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = true;
      }
      if (error) error.hidden = true;
      if (success) success.hidden = true;
    }

    applyInitialSearch() {
      const search = new URLSearchParams(window.location.search).get('location');
      if (!search || !this.geocoder) return;
      try {
        this.geocoder.query(search);
      } catch (error) {
        console.error('[Provider Locator] Initial location search failed.', error);
      }
    }

    setStatus(message, options = {}) {
      if (!this.statusNode) return;
      this.statusNode.replaceChildren(document.createTextNode(message));
      if (options.editFilters) {
        this.statusNode.appendChild(document.createTextNode(' '));
        const editFilters = document.createElement('button');
        editFilters.type = 'button';
        editFilters.className = 'locator-status__action';
        editFilters.dataset.action = 'open-custom-modal';
        editFilters.dataset.modalId = 'locator-filters';
        editFilters.textContent = 'Edit filters…';
        this.statusNode.appendChild(editFilters);
      }
    }

    fail(message) {
      this.root.dataset.locatorState = 'error';
      this.mapNode?.setAttribute('aria-busy', 'false');
      this.setStatus(message);
      if (this.resultsNode) this.resultsNode.replaceChildren(this.createEmptyState());
    }
  }

  function boot() {
    document.querySelectorAll('[data-provider-locator]').forEach(root => {
      if (root.__providerLocator) return;
      const locator = new ProviderLocator(root);
      root.__providerLocator = locator;
      locator.init();
    });
  }

  window.ProviderLocatorV2 = {
    ProviderLocator,
    helpers: { asBoolean, normalizeProvider, normalizedKey, slugify, splitValues, taxonomyKey },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
